import { useIntl } from 'react-intl'

import type {
  AllListingsFilters,
  AllListingsApiAllListingsRequest,
} from '@b-stock/search-api-client'

import type { ListingFiltersState } from '@/components/listings/search/ListingSearchProvider'
import type { KeysMatching } from '@/helpers/types/KeysMatching'

import CategoricalFilter from '../shared/CategoricalFilter'
import {
  useFilterOptions,
  createCategoricalAppliedComponent,
} from '../shared/filterUtils'
import toDisplayNames from '../toDisplayNames'
import type { ListingFilter, FilterProps } from '../types'

// TODO (BP-853): Remove this type extension when backend supports deals filter
// Extends API types to include deals filter for type safety
type ExtendedAllListingsFilters = AllListingsFilters & {
  deals?: Array<{ value: string; count?: number }>
}

type ExtendedAllListingsRequest = AllListingsApiAllListingsRequest & {
  deals?: string[]
}

export default function buildSimpleStringFilter({
  key,
  expandedOnInit,
  sortOrder,
  normalizeValue,
}: {
  key: keyof ListingFiltersState &
    keyof ExtendedAllListingsFilters &
    keyof ExtendedAllListingsRequest &
    KeysMatching<typeof toDisplayNames, (option: string) => React.ReactNode>
  expandedOnInit: boolean
  sortOrder?: 'row' | 'column'
  // See useFilterOptions - collapses raw values that are synonyms (e.g. a
  // renamed enum value that duplicates a current one) into a single option.
  normalizeValue?: (value: string) => string
}) {
  const toDisplayName = toDisplayNames[key]
  const SimpleStringFilter: ListingFilter<Set<string>> = ({
    onChange,
    desktopScreen,
    items: itemsData,
    value,
  }: FilterProps<Set<string>>) => {
    const intl = useIntl()
    const items = useFilterOptions(itemsData, toDisplayName, normalizeValue)

    return (
      <CategoricalFilter
        items={items}
        selected={value}
        name={intl.formatMessage({
          id: `Filters.${key}`,
        })}
        onChange={onChange}
        desktopScreen={desktopScreen}
        sortOrder={sortOrder}
      />
    )
  }

  SimpleStringFilter.expandedOnInit = expandedOnInit
  SimpleStringFilter.getOwnProps = ({ state, data, updateFilters }) => {
    return {
      filterKey: key,
      items: data?.allFilters[key] || [],
      value: new Set(state.filters[key]),
      onChange: (value: Set<string>) => updateFilters({ [key]: [...value] }),
    }
  }

  SimpleStringFilter.label = `Filters.${key}`

  SimpleStringFilter.Applied = createCategoricalAppliedComponent(
    key,
    SimpleStringFilter.label,
    toDisplayName
  )

  return SimpleStringFilter
}
